import React, { useCallback, useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { enUS, es } from 'date-fns/locale';
import { Box } from '@mui/material';
import MUIDataTable from 'mui-datatables';
import SupervisorFilters from 'components/supervisor/SupervisorFilters';
import AppBarButtonLink from 'components/shared/buttons/AppBarButtonLink';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import routes from 'common/routes';
import { getSupervisorColumns } from 'common/table/columns';
import { getSupervisorData } from 'common/table/data';
import { defaultTableOptions, getSelectToolBar, tableLocalization } from 'common/table/utils';
import AppBarContext from 'context/AppBarContext';
import supervisorSlice from 'store/modules/supervisor';

function SupervisorList() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(['buttons', 'labels', 'routes', 'tables']);
  const dateLocale = i18n.language === 'es' ? es : enUS;
  const pageTitle = t('supervisor.list', { ns: 'routes' });
  const emptyMessage = t('empty', { type: pageTitle.toLowerCase(), ns: 'tables' });
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const filters = useSelector((state) => state.supervisor.filters);
  const pagination = useSelector((state) => state.supervisor.pagination);
  const paginatorInfo = useSelector((state) => state.supervisor.paginatorInfo);
  const refresh = useSelector((state) => state.supervisor.refresh);
  const supervisors = useSelector((state) => state.supervisor.users);

  const loading = useSelector((state) => state.supervisor.status.general === 'loading');

  useEffect(() => {
    setAppBarButton(
      <AppBarButtonLink label={t('add', { ns: 'buttons' })} path={routes.createSupervisor.path} />
    );
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle, t]);

  const tableData = getSupervisorData(supervisors, t, dateLocale);
  const columns = getSupervisorColumns({ data: tableData, translateFunc: t });

  const onChangePage = (page) => {
    dispatch(supervisorSlice.actions.updatePagination({ page: page + 1 }));
  };

  const onDelete = (ids) => {
    dispatch(supervisorSlice.actions.remove(ids));
  };

  const onRestore = (ids) => {
    dispatch(supervisorSlice.actions.restore(ids));
  };

  const options = {
    ...defaultTableOptions,
    count: paginatorInfo.total,
    customToolbarSelect: (selectedRows, displayData) =>
      getSelectToolBar({
        filters,
        rows: selectedRows,
        data: displayData,
        onDelete,
        onRestore
      }),
    onChangePage,
    page: pagination.page - 1,
    rowsPerPage: pagination.first,
    textLabels: tableLocalization(pageTitle.toLowerCase(), t)
  };

  const list = useCallback(() => {
    dispatch(
      supervisorSlice.actions.list({
        filters,
        pagination
      })
    );
  }, [dispatch, filters, pagination]);

  useEffect(() => {
    list();
  }, [list, refresh]);

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <SupervisorFilters />
      <Box sx={{ mt: 3 }}>
        {loading ? (
          <LoadingBlock />
        ) : (
          <MUIDataTable
            title={supervisors.length === 0 ? emptyMessage : ''}
            data={tableData}
            columns={columns}
            options={options}
          />
        )}
      </Box>
    </>
  );
}

export default SupervisorList;